import { format, parseISO } from "date-fns";
import type { ReactNode } from "react";
import type { FieldDef, FormKpiElement, FormSummaryElement, FormSummaryToken } from "../core";
import { enumLabel, refLabel } from "./cell_utils";
import { appIcon } from "./icons";

// FormSummary — the scrolling header card (form-view revamp Enh 14): eyebrow +
// title + subtitle (literal text interleaved with <field> value tokens) + a KPI
// stat strip. Read-only; resolves field values from the live record draft.
//
// KPI value sources: `field=` reads the record directly; `metric=` / `formula=`
// are resolved by the backend (presentation._resolve_summary_kpis) and ride the
// record payload under deterministic synthetic keys — `__kpi__metric:<key>` /
// `__kpi__formula:<expr>` — that both sides derive from the render plan. Values
// are formatted by the field's declared type (date → "26 Jun", reference →
// display name, enum → label); pre-formatted Char labels (e.g. backend-composed
// money / validity) pass through verbatim; resolved metric/formula numbers get
// locale formatting.

type Rec = Record<string, unknown> | null | undefined;

interface FormSummaryProps {
    summary: FormSummaryElement;
    record: Rec;
    /** Field defs for the model — drives type-aware KPI value formatting. */
    fieldDefs?: FieldDef[];
}

function fmtDate(value: unknown): string {
    try {
        return format(parseISO(String(value)), "d MMM yyyy");
    } catch {
        return String(value ?? "");
    }
}

function fmtDayMonth(value: unknown): string {
    try {
        return format(parseISO(String(value)), "d MMM");
    } catch {
        return String(value ?? "");
    }
}

function formatValue(value: unknown, displayFormat?: string | null): string {
    if (value == null || value === "") return "";
    if (Array.isArray(value)) {
        // x2m chip list ([{id, display_name, data?}, …]) vs enum pair [key, label]
        if (value.length > 0 && typeof value[0] === "object" && value[0] !== null) {
            return value.map((v) => refLabel(v, displayFormat)).filter(Boolean).join(", ");
        }
        return enumLabel(value);
    }
    if (typeof value === "object") return refLabel(value, displayFormat); // m2o {id, display_name, data?}
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
}

// Resolve tokens to a display string. Tokens are concatenated verbatim (the
// parser preserves authored whitespace), so "Quote · v" + 1 → "Quote · v1"
// and field · field with " · " text stays "A · B". A field token's
// `displayFormat` (XML display_format="…") re-renders m2o/x2m chips from
// their `data` payload (e.g. "{name}" drops the "[CODE] " prefix).
function renderTokens(tokens: FormSummaryToken[], record: Rec): string {
    return tokens
        .map((t) => (t.type === "text" ? t.value : formatValue(record?.[t.name], t.displayFormat)))
        .join("")
        .trim();
}

// Chip tones → the theme's pill vocabulary (same classes the list views use).
const CHIP_TONE: Record<string, string> = {
    info: "pill--information",
    good: "pill--positive",
    warn: "pill--warning",
    bad: "pill--negative",
    neutral: "pill--neutral",
};

/** Meta-strip renderer (subtitle): field tokens with `icon=` / `chip=` render
    as distinct facts — an icon-led item or a tone pill — instead of one
    congested text run. Plain text tokens (e.g. " · ") stay as separators; a
    subtitle with no icon/chip tokens falls back to the flat string path. */
function renderMetaNodes(tokens: FormSummaryToken[], record: Rec): ReactNode[] {
    const nodes: ReactNode[] = [];
    tokens.forEach((t, i) => {
        if (t.type === "text") {
            const text = t.value.trim();
            if (text) nodes.push(<span key={`t-${i}`} className="form-summary__meta-sep">{text}</span>);
            return;
        }
        const label = formatValue(record?.[t.name], t.displayFormat);
        if (!label) return;
        const Icon = t.icon ? appIcon(t.icon) : null;
        if (t.chip) {
            const tone = CHIP_TONE[t.chip] ?? CHIP_TONE.neutral;
            nodes.push(
                <span key={`f-${i}`} className={`pill ${tone} form-summary__meta-chip`}>
                    {Icon ? <Icon size={12} /> : null}
                    {label}
                </span>,
            );
            return;
        }
        nodes.push(
            <span key={`f-${i}`} className="form-summary__meta-item">
                {Icon ? <Icon size={13} className="form-summary__meta-icon" /> : null}
                {label}
            </span>,
        );
    });
    return nodes;
}

/** Synthetic record key the backend resolver injects for a non-field KPI —
    MUST mirror PresentationKernel._kpi_record_key. */
function kpiRecordKey(kpi: FormKpiElement): string | null {
    if (kpi.metric) return `__kpi__metric:${kpi.metric}`;
    if (kpi.formula) return `__kpi__formula:${kpi.formula}`;
    return null;
}

function kpiValue(kpi: FormKpiElement, record: Rec, typeByName: Map<string, string>): string {
    if (!kpi.field) {
        // metric / formula — read the backend-resolved synthetic key.
        const key = kpiRecordKey(kpi);
        const raw = key ? record?.[key] : undefined;
        if (raw == null || raw === "") return "";
        if (typeof raw === "number") {
            return raw.toLocaleString(undefined, { maximumFractionDigits: 2 });
        }
        return formatValue(raw);
    }
    const raw = record?.[kpi.field];
    const type = typeByName.get(kpi.field);
    if (type === "date") return raw == null || raw === "" ? "" : fmtDayMonth(raw);
    if (type === "datetime") return raw == null || raw === "" ? "" : fmtDate(raw);
    return formatValue(raw);
}

function kpiLabel(kpi: FormKpiElement): string {
    return kpi.label || kpi.field || kpi.metric || kpi.formula || "";
}

function emphasisClass(kpi: FormKpiElement, value: string): string {
    const e = kpi.emphasis;
    if (!e) return "";
    if (e === "accent" || e === "good" || e === "warn") return `form-summary__kpi-value--${e}`;
    const m = /^warn-(below|above):(-?[\d.]+)$/.exec(e);
    if (m) {
        const num = Number.parseFloat(value.replace(/[^0-9.\-]/g, ""));
        const threshold = Number.parseFloat(m[2]);
        if (!Number.isNaN(num) && !Number.isNaN(threshold)) {
            if ((m[1] === "below" && num < threshold) || (m[1] === "above" && num > threshold)) {
                return "form-summary__kpi-value--warn";
            }
        }
    }
    return "";
}

export function FormSummary({ summary, record, fieldDefs }: FormSummaryProps) {
    const typeByName = new Map((fieldDefs ?? []).map((fd) => [fd.name, fd.type]));
    const eyebrow = renderTokens(summary.eyebrow ?? [], record);
    const title = renderTokens(summary.title, record);
    // Subtitle: icon/chip tokens upgrade it to the meta strip; otherwise the
    // flat joined-string rendering (author keeps " · " separators).
    const isMetaStrip = summary.subtitle.some((t) => t.type === "field" && (t.icon || t.chip));
    const subtitle = isMetaStrip ? null : renderTokens(summary.subtitle, record);
    const metaNodes = isMetaStrip ? renderMetaNodes(summary.subtitle, record) : [];
    const hasKpis = summary.kpis.length > 0;

    if (!eyebrow && !title && !subtitle && metaNodes.length === 0 && !hasKpis) return null;

    return (
        <div className="form-summary" data-viewid="ede-form-summary">
            {eyebrow ? <div className="form-summary__eyebrow">{eyebrow}</div> : null}
            {title ? <div className="form-summary__title">{title}</div> : null}
            {subtitle ? <div className="form-summary__subtitle">{subtitle}</div> : null}
            {metaNodes.length > 0 ? (
                <div className="form-summary__subtitle form-summary__subtitle--meta">{metaNodes}</div>
            ) : null}
            {hasKpis ? (
                <div className="form-summary__kpis">
                    {summary.kpis.map((kpi, i) => {
                        const value = kpiValue(kpi, record, typeByName);
                        return (
                            <div className="form-summary__kpi" key={`${kpiLabel(kpi)}-${i}`}>
                                <div className="form-summary__kpi-label">{kpiLabel(kpi)}</div>
                                <div className={`form-summary__kpi-value ${emphasisClass(kpi, value)}`.trim()}>
                                    {value || "—"}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
